import { PlayerProfile, ResearchJson, ResearchNodeView, researchTree, researchView } from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";

/**
 * Gold Research as use cases (M35, docs/RESEARCH.md): the `research` save section, the per-tree views the research
 * screen draws, and the summary the Shop's Gold page, the Towers tab and the Guide read. The rules are @highmarch/domain
 * meta/research.ts (buyResearch, researchView, withResearch).
 *
 * Save: the owned node ids live in their own section (saveSections.ts), read and written with the catalog
 * (LevelCatalog.fromSave / applyTo). A save from before research has no section and loads with nothing researched —
 * that is the whole migration (no schema bump: an absent section is the empty book).
 */

function asResearch(v: unknown): ResearchJson | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) && Array.isArray((v as ResearchJson).owned) ? (v as ResearchJson) : undefined;
}

export const researchSection = registerSaveSection<ResearchJson>("research", asResearch, (v) => v);

/** Loads the section into the profile's book (nothing researched when absent or unreadable). */
export function researchFromSave(save: HasSections, profile: PlayerProfile): void { profile.research.load(profile.balance, researchSection.read(save)); }
export function researchToSave(save: HasSections, profile: PlayerProfile): void { researchSection.write(save, profile.research.toJson()); }

/** One tree (a tower's, or the Fortress's) as the research screen draws it. */
export interface ResearchTreeView {
  readonly towerIndex: number;
  readonly towerId: string;
  readonly nodes: readonly ResearchNodeView[];
  readonly owned: number;
  /** Nodes open to buy now (gate and requirements met), affordable or not. */
  readonly available: number;
}

export function researchTreeView(profile: PlayerProfile, towerIndex: number): ResearchTreeView {
  const nodes = researchTree(profile.balance, towerIndex).map((n) => researchView(profile, n));
  return {
    towerIndex, towerId: profile.balance.towers[towerIndex]?.id ?? "", nodes,
    owned: nodes.filter((v) => v.state === "owned").length, available: nodes.filter((v) => v.state === "available").length,
  };
}

/** What the Shop row, the badges and the Bram hint need, without drawing anything. */
export interface ResearchSummary {
  /** Nodes open to buy now (gate and requirements met), affordable or not. */
  readonly available: number;
  /** Of those, the ones the player has the gold for. */
  readonly affordable: number;
  /** The cheapest open node's price, or null when none is open. */
  readonly cheapestCost: number | null;
  /** The cheapest open node's id (the screen opens on its tree), or null. */
  readonly cheapestId: string | null;
  readonly owned: number;
  readonly total: number;
}

/**
 * The research summary for `profile` (pure, cheap: one pass over the nodes). A tower that is not unlocked yet is still
 * at level 1, under every gate, so its tree never counts as open.
 */
export function researchSummary(profile: PlayerProfile): ResearchSummary {
  let available = 0, affordable = 0, cheapest: { id: string; gold: number } | null = null;
  for (const n of profile.balance.research.nodes) {
    const v = researchView(profile, n);
    if (v.state !== "available") continue;
    available++;
    if (v.affordable) affordable++;
    if (cheapest === null || n.gold < cheapest.gold) cheapest = { id: n.id, gold: n.gold };
  }
  return { available, affordable, cheapestCost: cheapest?.gold ?? null, cheapestId: cheapest?.id ?? null, owned: profile.research.count, total: profile.balance.research.nodes.length };
}
